import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import SideMenu from './SideMenu';

// Candidate icon SVG component
const CandidateIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.5 3.5H17.7C19.4913 3.5 20.387 3.5 20.9435 4.0565C21.5 4.61299 21.5 5.50866 21.5 7.3V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M17.5 20.5H17.7C19.4913 20.5 20.387 20.5 20.9435 19.9435C21.5 19.387 21.5 18.4913 21.5 16.7V16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M6.5 3.5H6.3C4.50866 3.5 3.61299 3.5 3.0565 4.0565C2.5 4.61299 2.5 5.50866 2.5 7.3V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M6.5 20.5H6.3C4.50866 20.5 3.61299 20.5 3.0565 19.9435C2.5 19.387 2.5 18.4913 2.5 16.7V16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M7.21484 15.7847C7.68758 15.1024 8.37508 14.5254 9.21678 14.1204C10.0585 13.7155 11.0187 13.5 12 13.5C12.9813 13.5 13.9415 13.7155 14.7832 14.1204C15.6249 14.5254 16.3124 15.1024 16.7852 15.7847" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SalesArr = [
  {
    label: 'Job Leads',
    to: '/sales/job-leads',
  },
  {
    label: 'Interviews',
    to: '/sales/interviews',
  },
  {
    label: 'Projects',
    to: '/sales/projects',
  },
  {
    label: 'Vendors',
    to: '/sales/vendors',
  },
  {
    label: 'Contacts',
    to: '/sales/contacts',
  },
  {
    label: 'Technologies',
    to: '/sales/technologies',
  },
  {
    label: 'Sales Teams',
    to: '/sales/sales-teams',
  },
];

const trainingArr = [
  { label: 'Classes', to: '/training/classes' },
  { label: 'Faculty', to: '/training/faculty' },
  { label: 'Trainings', to: '/training/trainings' },
];

const adminArr = [
  { label: 'Account', to: '/admin/account' },
  { label: 'Users', to: '/admin/users' },
  { label: 'User Roles', to: '/admin/user-roles' },
  { label: 'Settings', to: '/admin/settings' },
  { label: 'Configs', to: '/admin/system-configs' },
];
const menuItems = [
  { label: 'Dashboard', to: '/dashboard', icon: '/images/dashboard-icon.svg' },
  { label: 'Candidate', to: '/candidate', icon: CandidateIcon },
  {
    label: 'Sales',
    to: '/sales',
    icon: '/images/sales-icon.svg',
    children: SalesArr.map((m) => ({ ...m, icon: '/images/child-icon.svg' })),
  },
  {
    label: 'Training',
    to: '/training',
    icon: '/images/training-icon.svg',
    children: trainingArr.map((m) => ({ ...m, icon: '/images/child-icon.svg' })),
  },
  {
    label: 'Admin',
    to: '/admin',
    icon: '/images/adminstration-icon.svg',
    children: adminArr.map((m) => ({ ...m, icon: '/images/child-icon.svg' })),
  },
];

const Sidebar = ({ open, onClose }) => {
  const location = useLocation();
  const companiesClient = location.state?.companiesClient;

  const currentAdminArr = useMemo(() => {
    if (!companiesClient) return adminArr;
    const result = [...adminArr];
    const accountIdx = result.findIndex((item) => item.label === 'Account');
    result.splice(accountIdx + 1, 0, {
      label: 'Company',
      to: '/admin/account',
      alwaysActive: true,
      extraIndent: true,
      navState: location.state,
    });
    return result;
  }, [companiesClient, location.state]);

  const dynamicMenuItems = useMemo(() => [
    { label: 'Dashboard', to: '/dashboard', icon: '/images/dashboard-icon.svg' },
    { label: 'Candidate', to: '/candidate', icon: CandidateIcon },
    {
      label: 'Sales',
      to: '/sales',
      icon: '/images/sales-icon.svg',
      children: SalesArr.map((m) => ({ ...m, icon: '/images/child-icon.svg' })),
    },
    {
      label: 'Training',
      to: '/training',
      icon: '/images/training-icon.svg',
      children: trainingArr.map((m) => ({ ...m, icon: '/images/child-icon.svg' })),
    },
    {
      label: 'Admin',
      to: '/admin',
      icon: '/images/adminstration-icon.svg',
      children: currentAdminArr.map((m) => ({ ...m, icon: m.icon || '/images/child-icon.svg' })),
    },
  ], [currentAdminArr]);

  return (
    <aside
      id="sidebar-multi-level-sidebar"
      className={`fixed left-0 top-[52px] h-[calc(100vh-52px)] w-64 overflow-y-auto overflow-x-hidden transition-transform z-30 bg-primary-500 scrollbar-hide
        ${open ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0`}
      style={{
        scrollbarWidth: 'none', /* Firefox */
        msOverflowStyle: 'none', /* IE and Edge */
      }}
      aria-label="Sidebar"
    >
      {/* Close button for mobile */}
      <button
        className="absolute top-4 right-4 md:hidden text-2xl"
        onClick={onClose}
        aria-label="Close sidebar"
      >
        &times;
      </button>
      <SideMenu menuItems={dynamicMenuItems} />
    </aside>
  );
};

export default Sidebar;
